import { Button as MButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { Themes } from '../../constants';
import {
  FORTA_BLACK,
  FORTA_ERROR,
  FORTA_GRAY4,
  FORTA_GRAY2,
  FORTA_BLUE,
  FORTA_WHITE,
} from '../../utils/colors';
import { getThemeColor } from '../../utils/common';

import type { ButtonProps as MButtonProps } from '@mui/material';
import type { ReactNode } from 'react';

interface ButtonProps extends MButtonProps {
  loading?: boolean;
  colorTheme?: Themes;
  lightBackground?: boolean;
  textTransform?: string;
  error?: boolean | number;
  endIcon?: ReactNode | null;
  secondary?: boolean;
}

const StyledButton = styled((props: MButtonProps) => <MButton {...props} />, {
  shouldForwardProp: (prop) =>
    prop !== 'loading' &&
    prop !== 'colorTheme' &&
    prop !== 'lightBackground' &&
    prop !== 'error' &&
    prop !== 'textTransform',
})<ButtonProps>(({ colorTheme, lightBackground, error }) => ({
  borderRadius: '8px',
  backgroundColor: lightBackground
    ? getThemeColor(colorTheme).light3
    : getThemeColor(colorTheme).primary,
  color: lightBackground ? FORTA_BLACK : FORTA_WHITE,
  height: 40,

  '&.Mui-disabled': {
    backgroundColor: getThemeColor(colorTheme).light2,
    color: FORTA_WHITE,
  },
  '&:hover': {
    backgroundColor: lightBackground
      ? getThemeColor(colorTheme).light3
      : getThemeColor(colorTheme).primary,
    color: lightBackground ? FORTA_BLACK : FORTA_WHITE,
  },
  '&:after': error
    ? {
        content: `"${error === true ? '!' : error}"`,
        width: 21,
        height: 21,
        backgroundColor: FORTA_ERROR,
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        color: FORTA_WHITE,
        fontSize: '12px',
        fontWeight: 'bold',
        borderRadius: '50%',
        position: 'absolute',
        right: -5,
        top: -10,
      }
    : {},
}));

const FHButton = (props: ButtonProps) => {
  const {
    disabled,
    loading = false,
    colorTheme = Themes.Primary,
    lightBackground = false,
    textTransform = 'Uppercase',
    children,
    error = false,
    secondary = false,
    sx = {},
  } = props;

  return (
    <StyledButton
      {...props}
      sx={
        secondary
          ? {
              backgroundColor: FORTA_WHITE,
              border: `1px solid ${FORTA_BLUE.light2}`,
              color: FORTA_GRAY4,
              '&:hover': {
                backgroundColor: FORTA_WHITE,
                border: `1px solid ${FORTA_BLUE.light2}`,
                color: FORTA_GRAY4,
              },
              '&.Mui-disabled': {
                backgroundColor: FORTA_WHITE,
                color: FORTA_GRAY2,
              },
              ...sx,
            }
          : { border: `1px solid ${FORTA_BLUE.light1}`, ...sx }
      }
      variant="contained"
      disabled={loading || disabled}
      colorTheme={colorTheme}
      lightBackground={lightBackground}
      error={error}
    >
      <Typography variant="button" sx={{ textTransform }}>
        {loading ? 'Loading...' : children}
      </Typography>
    </StyledButton>
  );
};

export default FHButton;
